import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api, ApiError } from "../api/client";
import { ADMIN_CREATE_ROLES, roleLabel } from "../lib/admin";

export function NewUserPage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("staff");
  const [password, setPassword] = useState("");
  const [buildingIds, setBuildingIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  function toggleBuilding(id: number) {
    setBuildingIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  const createMut = useMutation({
    mutationFn: () =>
      api.admin.create({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        role,
        password,
        buildingIds: role === "staff" ? buildingIds : [],
      }),
    onSuccess: ({ id }) => navigate(`/admin/${id}`),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError("That email address is already in use.");
      } else {
        setError("Could not create the user. Please try again.");
      }
    },
  });

  function onSave() {
    setError(null);
    if (!fullName.trim()) return setError("Please enter the person's name.");
    if (!email.trim() || !email.includes("@")) return setError("Please enter a valid email.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    createMut.mutate();
  }

  return (
    <Layout title="Add a New User">
      <p className="help-line">
        New accounts are <strong>Staff</strong> or <strong>Maintenance</strong>. Share the login
        details with the person through your approved process.
      </p>
      <section className="card form-card">
        <label className="field">
          <span>Full name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </label>

        <div className="two-col">
          <label className="field">
            <span>Email (used to log in)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
            />
          </label>
          <label className="field">
            <span>Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </label>
        </div>

        <div className="two-col">
          <label className="field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              {ADMIN_CREATE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Temporary password</span>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
          </label>
        </div>

        {role === "staff" && (
          <div className="field">
            <span>Building access</span>
            <fieldset className="choice-set">
              {(buildingsQ.data?.buildings ?? []).map((b) => (
                <label key={b.id} className="choice">
                  <input
                    type="checkbox"
                    checked={buildingIds.includes(b.id)}
                    onChange={() => toggleBuilding(b.id)}
                  />
                  <span>{b.name}</span>
                </label>
              ))}
            </fieldset>
          </div>
        )}
        {role === "maintenance" && (
          <p className="sub">
            Maintenance users are not building-scoped — they see only the work orders assigned to
            them.
          </p>
        )}

        {error && <p className="field-error">{error}</p>}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-xl"
            onClick={() => navigate("/admin")}
            disabled={createMut.isPending}
          >
            Cancel
          </button>
          <button className="btn btn-primary btn-xl" onClick={onSave} disabled={createMut.isPending}>
            {createMut.isPending ? "Saving…" : "Create user"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
